import CarriedInterestPartnerSettings from './CarriedInterestPartnerSettings';
import { useLayoutContext } from 'contexts/LayoutContext';
import PageLoader from 'components/PageLoader';
import OverallCarriedInterestSettings from './OverallCarriedInterestSettings';
import CarriedInterestFeeTemplates from './CarriedInterestFeeTemplates';
import VerticalTabs from 'components/new/VerticalTabs';
import { useVerticalTabs } from 'components/new/VerticalTabs/useVerticalTabs';
import { Button, styled } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import FullHeightView from 'components/new/FullHeightView';
import {
  CarrySettingsProvider,
  useCarrySettingsContext,
} from './CarrySettingsContext';

const HorizontalPaddedColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'isHidden' && prop !== 'paddingBottom',
})<{ paddingBottom?: boolean; isHidden?: boolean }>(
  ({ theme, paddingBottom, isHidden }) => ({
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(theme.spacingSizes.lg, 0, 0, 0),
      ...(paddingBottom && {
        paddingBottom: theme.spacing(theme.spacingSizes.lg),
      }),
    },
    paddingBottom: paddingBottom ? theme.spacing(theme.spacingSizes.lg) : 0,
    ...(isHidden && { display: 'none' }),
  }),
);

enum Tab {
  OverallSettings = 'overall-settings',
  FeeTemplates = 'fee-templates',
  PartnerSettings = 'partner-settings',
}

const formIdByTab: Record<Tab, string> = {
  [Tab.OverallSettings]: 'carry-settings-form-overall',
  [Tab.FeeTemplates]: 'carry-settings-form-templates',
  [Tab.PartnerSettings]: 'carry-settings-form-partners',
};

const tabs: Array<{ label: string; id: Tab }> = [
  { label: 'Overall carry settings', id: Tab.OverallSettings },
  { label: 'Carry templates', id: Tab.FeeTemplates },
  { label: 'Carry partner settings', id: Tab.PartnerSettings },
];

const CarrySettingsInner: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const { isPending } = useCarrySettingsContext();
  const { selectedTab, setSelectedTab } = useVerticalTabs<Tab>(
    Tab.OverallSettings,
  );

  if (!vehicle) return <PageLoader />;

  return (
    <FullHeightView
      actionBar={
        <Row justify="flex-end">
          <Button
            size="large"
            variant="contained"
            type="submit"
            form={formIdByTab[selectedTab]}
            loading={isPending}
          >
            Save changes
          </Button>
        </Row>
      }
    >
      <VerticalTabs
        fullHeight
        tabs={tabs}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        responsive
        renderContent={() => (
          <>
            <HorizontalPaddedColumn
              paddingBottom
              fullWidth
              isHidden={selectedTab !== Tab.OverallSettings}
            >
              <OverallCarriedInterestSettings vehicle={vehicle} />
            </HorizontalPaddedColumn>
            <HorizontalPaddedColumn
              fullWidth
              isHidden={selectedTab !== Tab.FeeTemplates}
            >
              <CarriedInterestFeeTemplates vehicle={vehicle} />
            </HorizontalPaddedColumn>
            <HorizontalPaddedColumn
              fullWidth
              isHidden={selectedTab !== Tab.PartnerSettings}
            >
              <CarriedInterestPartnerSettings vehicle={vehicle} />
            </HorizontalPaddedColumn>
          </>
        )}
      />
    </FullHeightView>
  );
};

const CarrySettings: React.FC = () => (
  <CarrySettingsProvider>
    <CarrySettingsInner />
  </CarrySettingsProvider>
);

export default CarrySettings;
